import { z } from 'zod';
import { normalizeMarketCountryCode } from '@pkgs/components/utils/validation';
import { safeLocalStorage } from '@pkgs/components/utils/storageUtils';
import { Currency, isRecord, Language, MarketCountryCode, MeasurementSystem } from '@pkgs/api';

import { UnitEnum } from '@/types/unit';

import { type Settings } from '../providers/SettingsProvider';

const SETTINGS_KEY = 'settings';

const settingsSchema = z.object({
  currency: z.string().toUpperCase().pipe(z.enum(Currency)).optional(),
  measurementSystem: z.enum(MeasurementSystem).optional(),
  language: z.enum(Language).optional(),
  unit: z.string().toUpperCase().pipe(z.enum(UnitEnum)).optional(),
  country: z.string().transform(normalizeMarketCountryCode).pipe(z.enum(MarketCountryCode)).optional(),
  countryCode: z.string().transform(normalizeMarketCountryCode).pipe(z.enum(MarketCountryCode)).optional(),
});

export const readSettingsFromStorage = (): Partial<Settings> | null => {
  const localSettings = safeLocalStorage.getItem(SETTINGS_KEY);

  if (!localSettings) return null;

  let parsedJson: unknown;

  try {
    parsedJson = JSON.parse(localSettings);
  } catch (error) {
    console.warn('Error parsing settings from local storage', error);
    return null;
  }

  const parsedSettings = settingsSchema.safeParse(parsedJson);

  if (!parsedSettings.success) {
    console.warn('Error parsing settings from local storage', parsedSettings.error.flatten().fieldErrors);
    return null;
  }

  const currency = parsedSettings.data.currency;
  let measurementSystem = parsedSettings.data.measurementSystem;
  if (parsedSettings.data.unit) {
    // some other teams use unit instead of measurementSystem as key in the local storage
    measurementSystem = parsedSettings.data.unit === UnitEnum.MT ? MeasurementSystem.metric : MeasurementSystem.imperial;
  }

  return {
    currency,
    measurementSystem,
  };
};

export const writeSettingsToStorage = (settings: Partial<Settings>, language: Language) => {
  // preserve any existing settings that we may not know about
  const currentSettings = safeLocalStorage.getItem(SETTINGS_KEY);
  let currentSettingsParsed: Record<string, unknown> = {};

  if (currentSettings) {
    try {
      const parsedCurrentSettings: unknown = JSON.parse(currentSettings);
      currentSettingsParsed = isRecord(parsedCurrentSettings) ? parsedCurrentSettings : {};
    } catch (error) {
      console.warn('Error parsing existing settings from local storage', error);
    }
  }

  try {
    safeLocalStorage.setItem(
      SETTINGS_KEY,
      JSON.stringify({
        ...currentSettingsParsed,
        ...settings,
        // some other teams use unit instead of measurementSystem as key in the local storage
        unit: settings.measurementSystem === MeasurementSystem.metric ? UnitEnum.MT : UnitEnum.FT,
        language,
      }),
    );
  } catch (error) {
    console.warn('Error writing settings to local storage', error);
  }
};
